import React, { useState } from "react";
import { useCompare } from "@/context/CompareContext";
import { useSettings } from "@/context/SettingsContext";
import { fileUrl, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GitCompareArrows, X } from "lucide-react";

const rows = [
  { key: "price", label: "Price", fmt: (p) => formatPrice(p.price, p.price_unit) },
  { key: "listing_type", label: "Listing", fmt: (p) => p.listing_type },
  { key: "property_type", label: "Type", fmt: (p) => p.property_type },
  { key: "area_name", label: "Area", fmt: (p) => `${p.area_name || ""}${p.city ? ", " + p.city : ""}` },
  { key: "bedrooms", label: "Bedrooms", fmt: (p) => p.bedrooms ?? "—" },
  { key: "bathrooms", label: "Bathrooms", fmt: (p) => p.bathrooms ?? "—" },
  { key: "area_sqft", label: "Area (sqft)", fmt: (p) => p.area_sqft ?? "—" },
  { key: "facing", label: "Facing", fmt: (p) => p.facing || "—" },
  { key: "rera_license", label: "RERA", fmt: (p) => p.rera_license || "—" },
];

export default function CompareBar() {
  const { feature } = useSettings();
  const { items, remove, clear } = useCompare();
  const [open, setOpen] = useState(false);

  if (!feature("property_comparison") || items.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-brand-ink text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 max-w-[94vw]" data-testid="compare-bar">
        <GitCompareArrows className="w-5 h-5 text-brand-emerald shrink-0" />
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {items.map((p) => (
            <div key={p.id} className="flex items-center gap-1.5 bg-white/10 rounded-full pl-2 pr-1 py-1 text-xs whitespace-nowrap">
              <span className="max-w-[120px] truncate">{p.title}</span>
              <button onClick={() => remove(p.id)} className="opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <Button size="sm" onClick={() => setOpen(true)} disabled={items.length < 2} className="rounded-full bg-brand-emerald shrink-0" data-testid="compare-open">Compare ({items.length})</Button>
        <button onClick={clear} className="text-xs text-white/60 hover:text-white shrink-0">Clear</button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Property Comparison</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="compare-table">
              <thead>
                <tr>
                  <th className="text-left p-2 text-brand-muted font-medium"></th>
                  {items.map((p) => (
                    <th key={p.id} className="p-2 text-left">
                      <img src={fileUrl(p.photos?.[0])} alt="" className="w-full h-20 object-cover rounded-lg mb-1.5" />
                      <div className="font-semibold text-brand-ink line-clamp-1">{p.title}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-brand-line">
                    <td className="p-2 text-brand-muted font-medium">{r.label}</td>
                    {items.map((p) => <td key={p.id} className="p-2 text-brand-ink capitalize">{r.fmt(p)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
